import * as React from "react"
import { Check, ChevronsUpDown, Users } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useOfflineGroupsReadOnly } from "@/hooks/offline/useOfflineGroupsReadOnly"
// import { useUserGroups } from "@/hooks/queries/useGroups"
import { useActiveGroup } from "@/stores/group.store"

interface GroupSelectorProps {
  isMobile?: boolean
}

export function GroupSelector({ isMobile = false }: GroupSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const {
    groups = [],
    isLoading
  } = useOfflineGroupsReadOnly()
  const { activeGroupId, setActiveGroupId } = useActiveGroup()

  const selectedGroup = groups.find((g) => g.id === activeGroupId)
  const buttonClass = isMobile ? "w-full justify-between" : "w-[240px] justify-between"

  // Auto-select first group if no group is selected and groups are available
  React.useEffect(() => {
    if (!isLoading && groups.length > 0) {
      // If no group is selected, or the selected group no longer exists
      if (!activeGroupId || !groups.find(g => g.id === activeGroupId)) {
        setActiveGroupId(groups[0].id)
      }
    }
  }, [groups, activeGroupId, setActiveGroupId, isLoading])

  if (isLoading) {
    return (
      <Button variant="outline" className={buttonClass} disabled>
        <span className="truncate">Loading groups...</span>
      </Button>
    )
  }

  if (groups.length === 0) {
    return (
      <Button variant="outline" className={buttonClass} disabled>
        <span className="truncate">No groups available</span>
      </Button>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={buttonClass}
        >
          <div className="flex items-center gap-2 truncate">
            <Users className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {selectedGroup ? selectedGroup.name : "Select group..."}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={isMobile ? "w-full p-0" : "w-[240px] p-0"}>
        <Command>
          <CommandInput placeholder="Search groups..." />
          <CommandList>
            <CommandEmpty>No group found.</CommandEmpty>
            <CommandGroup>
              {groups.map((group) => (
                <CommandItem
                  key={group.id}
                  value={group.name}
                  className="cursor-pointer"
                  onSelect={() => {
                    // Always set the group, don't allow deselecting
                    setActiveGroupId(group.id)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      activeGroupId === group.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span>{group.name}</span>
                    {group.users && (
                      <span className="text-xs text-muted-foreground">
                        {group.users.length} members
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}